import {
  DEFAULT_BUTTON_NAVIGATION,
  LLMBlockTargetField,
} from '@conversed/shared/src/central/types/blocks/LLMBlock';
import { ThirdPartyCredentialsSchema } from '@conversed/shared/src/central/types/company';
import {
  DEFAULT_LLM_MODEL_ID,
  DEFAULT_LLM_MODEL_TYPE,
  LLMModelId,
  LLMModelType,
} from '@conversed/shared/src/services/llm/llm.type';
import { z } from 'zod';
import { createRESTContract } from '../../../';
import {
  DocumentType,
  ThirdPartySource,
} from '../../../contracts/data-sources/data-sources.enums';
import {
  DEFAULT_AUTOMATICALLY_INCLUDE_IN_LLM,
  LLMBlockSelectionSchema,
} from '../../../contracts/data-sources/data-sources.schema';

const ScoreSchema = z.number().lte(100).gt(0);

const PromptSchema = z.array(
  z.object({
    type: z.union([
      z.literal('user'),
      z.literal('assistant'),
      z.literal('system'),
    ]),
    message: z.string(),
  }),
);

const ResponseSchema = z.object({
  score: ScoreSchema,
  vectorMatches: z.array(
    z.object({
      type: z.string(),
      chunk: z.string(),
      mapped: z.unknown(),
      source: z.string(),
      label: z.string().optional(),
      score: z.number(),
    }),
  ),
  llmResponse: z.object({
    answer: z.string(),
    buttons: z.string().array().optional(),
    shortcuts: z.string().array().optional(),
  }),
  context: PromptSchema,
  modelId: z.string(),
});

export type TLLMResponseSchema = z.infer<typeof ResponseSchema>;

const LLMQueryConfigSchema = z
  .object({
    buttonNavigation: z
      .object({
        enabled: z.boolean().default(DEFAULT_BUTTON_NAVIGATION),
        buttonIds: z.string().array().default([]),
      })
      .optional(),
    guardRails: z
      .object({
        guardRailId: z.string(),
        guardRailVersion: z.string(),
      })
      .optional(),
  })
  .default({
    buttonNavigation: {
      enabled: false,
      buttonIds: [],
    },
  });

export type TLLMQueryConfig = z.infer<typeof LLMQueryConfigSchema>;

const LLMQuerySchema = z.object({
  scoreThreshold: ScoreSchema,
  modelId: z.enum(LLMModelId).default(DEFAULT_LLM_MODEL_ID),
  llmType: z.enum(LLMModelType).default(DEFAULT_LLM_MODEL_TYPE),
  question: z.string(),
  // Retrieval-only query. Callers may widen it with conversation context so a
  // short follow-up ("augustus") still searches for the original subject
  // instead of losing it. Falls back to `question` when absent.
  searchQuery: z.string().optional(),
  dataSources: z.string().array().optional(),
  knowledgeArticles: z.string().array().optional(),
  aiKnowledgeLabels: z.string().array().optional(),
  params: z.object({
    clientName: z.string().optional(),
    botName: z.string().optional(),
  }),
  prompt: PromptSchema,
  config: LLMQueryConfigSchema,
});

const S3RecordSchema = z.object({
  eventVersion: z.string(),
  eventSource: z.string(),
  awsRegion: z.string(),
  eventTime: z.string(),
  eventName: z.string(),
  userIdentity: z.object({
    principalId: z.string(),
  }),
  requestParameters: z.object({
    sourceIPAddress: z.string(),
  }),
  responseElements: z.object({
    'x-amz-request-id': z.string(),
    'x-amz-id-2': z.string(),
  }),
  s3: z.object({
    s3SchemaVersion: z.string(),
    configurationId: z.string(),
    bucket: z.object({
      name: z.string(),
      ownerIdentity: z.object({
        principalId: z.string(),
      }),
      arn: z.string(),
    }),
    object: z.object({
      key: z.string(),
      size: z.number(),
      eTag: z.string(),
      sequencer: z.string(),
    }),
  }),
});

const AmazonSNSSchema = z.object({
  MessageId: z.string(),
  TopicArn: z.string(),
  Subject: z.string(),
  Message: z.string(),
  Timestamp: z.string(),
  SignatureVersion: z.string(),
  Signature: z.string(),
  SigningCertURL: z.string(),
  UnsubscribeURL: z.string(),
});

const TrainSourceSchema = z.object({
  key: z.string(),
  companyId: z.string(),
  bucketName: z.string(),
  documentType: z.enum(
    Object.values(DocumentType) as [DocumentType, ...DocumentType[]],
  ),
  shouldReTrain: z.boolean().default(false),
  lastSuccessfulTrainingStartedAt: z.string().default(''),
  automaticallyIncludeInLLM: z
    .boolean()
    .default(DEFAULT_AUTOMATICALLY_INCLUDE_IN_LLM),
  fileType: z.string(),
  thirdPartySource: z
    .enum(
      Object.values(ThirdPartySource) as [
        ThirdPartySource,
        ...ThirdPartySource[],
      ],
    )
    .optional(),
  thirdPartyCredentials: ThirdPartyCredentialsSchema.optional(),
  sourceName: z.string().optional(),
  label: z.string().optional(),
  rankingTier: z.number().optional(),
  selectedLLMBlocks: LLMBlockSelectionSchema.array().optional(),
  // * Required for files from non-default document libraries (scanAllDrives). Absent for default-drive files.
  driveId: z.string().nullable().optional(),
  inlineContent: z.string().optional(),
  blockTargetField: z.nativeEnum(LLMBlockTargetField).optional(),
});

const DeleteSourceSchema = TrainSourceSchema.pick({
  key: true,
});

const DeleteTrainingDataByDateSchema = TrainSourceSchema.pick({
  key: true,
  lastSuccessfulTrainingStartedAt: true,
});

const RenameSourceSchema = z.object({
  oldKey: z.string(),
  newKey: z.string(),
});

const UpdateLabelSchema = z.object({
  key: z.string(),
  label: z.string(),
});

const UpdateRankingTierSchema = z.object({
  key: z.string(),
  rankingTier: z.number().nullable(),
});

export type TAmazonSNSSchema = z.infer<typeof AmazonSNSSchema>;
export type TS3RecordSchema = z.infer<typeof S3RecordSchema>;

export type TLLMQuerySchema = z.infer<typeof LLMQuerySchema>;
export type TTrainSourceSchema = z.infer<typeof TrainSourceSchema>;

const GenerateEmbeddingBodySchema = z.object({
  text: z.string(),
});

const GenerateEmbeddingResponseSchema = z.object({
  embedding: z.number().array(),
});

export type TGenerateEmbeddingBodySchema = z.infer<
  typeof GenerateEmbeddingBodySchema
>;
export type TGenerateEmbeddingResponseSchema = z.infer<
  typeof GenerateEmbeddingResponseSchema
>;

export const LLMContract = createRESTContract({
  getResponseToQuery: {
    // * vectorType can be of type "llm" or "query", this endpoint should use "query" going forward but has been changed to
    // * ":vectorType" for backwards compatibility, as "llm" is the older method which botcore might still call if not updated
    path: '/vector/:vectorType/response/:client',
    method: 'POST',
    response: ResponseSchema,
    body: LLMQuerySchema,
  },
  deleteTrainingData: {
    path: '/vector/llm/:client',
    method: 'DELETE',
    body: DeleteSourceSchema,
    response: z.void(),
  },
  deleteOldTrainingData: {
    path: '/vector/llm/:client/old',
    method: 'DELETE',
    body: DeleteTrainingDataByDateSchema,
    response: z.void(),
  },
  deleteNewerTrainingData: {
    path: '/vector/llm/:client/newer',
    method: 'DELETE',
    body: DeleteTrainingDataByDateSchema,
    response: z.void(),
  },
  renameTrainingData: {
    path: '/vector/llm/:client/rename',
    method: 'PATCH',
    body: RenameSourceSchema,
    response: z.void(),
  },
  updateTrainingDataLabel: {
    path: '/vector/llm/:client/label',
    method: 'PATCH',
    body: UpdateLabelSchema,
    response: z.void(),
  },
  updateTrainingDataRankingTier: {
    path: '/vector/llm/:client/ranking-tier',
    method: 'PATCH',
    body: UpdateRankingTierSchema,
    response: z.void(),
  },
  generateEmbedding: {
    path: '/vector/llm/:client/embedding',
    method: 'POST',
    body: GenerateEmbeddingBodySchema,
    response: GenerateEmbeddingResponseSchema,
  },
});
